import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import * as d3 from 'd3';
import { getApiEndpoint, vertexFetch } from '../../../constants.js';
import { fetchPreviewsBatch, fetchAllVertices, fetchAllVertexSets } from '../../services/api';
import { route } from '../HashRouter';
import type { VertexSetWithVertices } from '../../types';
import { FullscreenIcon, ExitFullscreenIcon } from '../icons';

interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  preview?: {
    title?: string;
    description?: string;
    image?: string;
  };
  hasEdges: boolean;
  cluster?: string;
  setName?: string;
}

interface GraphLink extends d3.SimulationLinkDatum<GraphNode> {
  source: string | GraphNode;
  target: string | GraphNode;
  info?: string;
}

interface EdgeResponse {
  source: { rendered: string };
  target: { rendered: string };
  edgeType: string;
  info?: string;
}

export interface GraphVizPageProps {
  onClose: () => void;
}

interface NodeContextMenuState {
  x: number;
  y: number;
  node: GraphNode;
}

interface EdgeContextMenuState {
  x: number;
  y: number;
  link: GraphLink;
}

interface EdgeCreationState {
  source: GraphNode;
  target: GraphNode;
}

export function GraphVizPage({ onClose }: GraphVizPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<{ nodes: GraphNode[]; links: GraphLink[] } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [contextMenu, setContextMenu] = useState<NodeContextMenuState | null>(null);
  const [edgeContextMenu, setEdgeContextMenu] = useState<EdgeContextMenuState | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<GraphNode | null>(null);
  const [edgeCreation, setEdgeCreation] = useState<EdgeCreationState | null>(null);
  const [edgeNote, setEdgeNote] = useState('');
  const [creatingEdge, setCreatingEdge] = useState(false);

  // Ref to access current connectingFrom state inside D3 event handlers
  const connectingFromRef = useRef<GraphNode | null>(null);
  connectingFromRef.current = connectingFrom;

  // Ref to preserve zoom transform across re-renders
  const zoomTransformRef = useRef<d3.ZoomTransform | null>(null);

  // Refs for D3 elements to allow direct manipulation without re-render
  const simulationRef = useRef<d3.Simulation<GraphNode, GraphLink> | null>(null);
  const linksGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const nodesGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const nodesDataRef = useRef<GraphNode[]>([]);
  const linksDataRef = useRef<GraphLink[]>([]);

  useEffect(() => {
    loadGraphData();
  }, []);

  // Render graph after data is loaded and container is mounted
  // Use ResizeObserver to re-render when container size changes significantly
  useEffect(() => {
    if (!graphData || !containerRef.current || !svgRef.current) return;

    let lastWidth = 0;
    let lastHeight = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const renderIfSizeChanged = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      // Only re-render if size changed significantly (more than 50px)
      if (Math.abs(width - lastWidth) > 50 || Math.abs(height - lastHeight) > 50) {
        lastWidth = width;
        lastHeight = height;
        if (width > 100 && height > 100) {
          renderGraph(graphData.nodes, graphData.links);
        }
      }
    };

    const observer = new ResizeObserver(() => {
      // Debounce resize events
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(renderIfSizeChanged, 100);
    });

    observer.observe(containerRef.current);

    // Initial render
    renderIfSizeChanged();

    return () => {
      observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [graphData]);

  // Handle keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setContextMenu(null);
        setEdgeContextMenu(null);
        setConnectingFrom(null);
        setEdgeCreation(null);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close context menus on click outside
  useEffect(() => {
    function handleClick() {
      setContextMenu(null);
      setEdgeContextMenu(null);
    }
    if (contextMenu || edgeContextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, edgeContextMenu]);

  async function createEdge(source: string, target: string, info: string | null) {
    const apiEndpoint = await getApiEndpoint();
    const body: Record<string, string> = { source, target };
    if (info) body.info = info;

    const response = await vertexFetch(`${apiEndpoint}/user/vertex/graph/edge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Failed to create edge: ${response.status}`);
    }
  }

  async function handleCreateEdge() {
    if (!edgeCreation) return;
    setCreatingEdge(true);
    try {
      const noteValue = edgeNote.trim() || null;
      await createEdge(
        edgeCreation.source.id,
        edgeCreation.target.id,
        noteValue
      );

      // Directly manipulate D3 without React re-render
      if (simulationRef.current && svgRef.current) {
        // Create new link object
        const newLink: GraphLink = {
          source: edgeCreation.source,
          target: edgeCreation.target,
          info: noteValue || undefined
        };

        // Add to data
        linksDataRef.current.push(newLink);

        // Update simulation's link force
        const linkForce = simulationRef.current.force('link') as d3.ForceLink<GraphNode, GraphLink>;
        linkForce.links(linksDataRef.current);

        // Mark nodes as having edges
        edgeCreation.source.hasEdges = true;
        edgeCreation.target.hasEdges = true;

        // Add DOM element for new link
        const svg = d3.select(svgRef.current);
        const linksGroup = svg.select('.links');

        const newLinkGroup = linksGroup.append('g')
          .datum(newLink)
          .attr('cursor', 'pointer')
          .on('contextmenu', (event, d) => {
            event.preventDefault();
            event.stopPropagation();
            setEdgeContextMenu({
              x: event.pageX,
              y: event.pageY,
              link: d
            });
          });

        // Add invisible wider line for click target
        newLinkGroup.append('line')
          .attr('stroke', 'transparent')
          .attr('stroke-width', 12);

        // Add visible line
        newLinkGroup.append('line')
          .attr('stroke', noteValue ? '#666' : '#999')
          .attr('stroke-opacity', 0.6)
          .attr('stroke-width', noteValue ? 2 : 1.5)
          .attr('stroke-dasharray', noteValue ? '5,3' : 'none')
          .attr('marker-end', 'url(#arrowhead)');

        // Add tooltip if has info
        if (noteValue) {
          newLinkGroup.append('title').text(noteValue);
        }

        // Brief restart to position the new link
        simulationRef.current.alpha(0.3).restart();
      }

      setEdgeCreation(null);
      setEdgeNote('');
    } catch (err) {
      console.error('[GraphVizPage] Failed to create edge:', err);
      alert('Failed to create connection. Please try again.');
    } finally {
      setCreatingEdge(false);
    }
  }

  async function handleDeleteEdge(link: GraphLink) {
    const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
    const targetId = typeof link.target === 'string' ? link.target : link.target.id;

    if (!confirm('Delete this connection?')) return;

    try {
      const apiEndpoint = await getApiEndpoint();
      const sourceVertex = encodeURIComponent(sourceId);
      const targetVertex = encodeURIComponent(targetId);

      const response = await vertexFetch(
        `${apiEndpoint}/user/vertex/graph/edge?sourceVertex=${sourceVertex}&targetVertex=${targetVertex}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete edge: ${response.status}`);
      }

      setEdgeContextMenu(null);

      // Directly manipulate D3 without React re-render
      if (simulationRef.current && linksGroupRef.current && svgRef.current) {
        // Remove link from data
        const linkIndex = linksDataRef.current.findIndex(l => {
          const lSourceId = typeof l.source === 'string' ? l.source : l.source.id;
          const lTargetId = typeof l.target === 'string' ? l.target : l.target.id;
          return lSourceId === sourceId && lTargetId === targetId;
        });

        if (linkIndex !== -1) {
          linksDataRef.current.splice(linkIndex, 1);

          // Update simulation's link force with new data
          const linkForce = simulationRef.current.force('link') as d3.ForceLink<GraphNode, GraphLink>;
          linkForce.links(linksDataRef.current);

          // Remove the DOM element for this link
          const svg = d3.select(svgRef.current);
          svg.select('.links').selectAll('g')
            .data(linksDataRef.current, (d: any) => {
              const s = typeof d.source === 'string' ? d.source : d.source.id;
              const t = typeof d.target === 'string' ? d.target : d.target.id;
              return `${s}->${t}`;
            })
            .exit()
            .remove();

          // Brief restart to update layout
          simulationRef.current.alpha(0.1).restart();
        }
      }
    } catch (err) {
      console.error('[GraphVizPage] Failed to delete edge:', err);
      alert('Failed to delete connection. Please try again.');
    }
  }

  async function loadGraphData() {
    try {
      const apiEndpoint = await getApiEndpoint();

      // Fetch all vertices, edges, and vertex sets in parallel
      const [edgesResponse, allVertices, allSets] = await Promise.all([
        vertexFetch(`${apiEndpoint}/user/vertex/graph/edges`),
        fetchAllVertices(),
        fetchAllVertexSets()
      ]);

      if (!edgesResponse.ok) {
        throw new Error(`Failed to fetch edges: ${edgesResponse.status}`);
      }

      const edges: EdgeResponse[] = await edgesResponse.json();
      console.log('[GraphVizPage] Fetched edges:', edges.length, 'vertices:', allVertices.length, 'sets:', allSets.length);

      // Build reverse index: vertex URL -> set IDs
      const vertexToSets = new Map<string, { setId: string; setName: string }[]>();
      allSets.forEach((setData: VertexSetWithVertices) => {
        setData.vertices.forEach(v => {
          const url = v.rendered;
          if (!vertexToSets.has(url)) {
            vertexToSets.set(url, []);
          }
          vertexToSets.get(url)!.push({
            setId: setData.vertexSet.id,
            setName: setData.vertexSet.name
          });
        });
      });

      // Build nodes from ALL vertices (not just those with edges)
      const nodeMap = new Map<string, GraphNode>();
      allVertices.forEach(v => {
        const url = v.vertex.rendered;
        const sets = vertexToSets.get(url) || [];

        // Determine cluster: by set if in a set, otherwise by domain
        let cluster: string;
        let setName: string | undefined;
        if (sets.length > 0) {
          cluster = `set:${sets[0].setId}`;
          setName = sets[0].setName;
        } else {
          try {
            const hostname = new URL(url).hostname;
            cluster = `domain:${hostname}`;
          } catch {
            cluster = 'domain:other';
          }
        }

        nodeMap.set(url, {
          id: url,
          label: extractLabel(url),
          hasEdges: false,
          cluster,
          setName
        });
      });

      // Mark nodes that have edges
      edges.forEach(edge => {
        const sourceUrl = edge.source.rendered;
        const targetUrl = edge.target.rendered;

        if (nodeMap.has(sourceUrl)) {
          nodeMap.get(sourceUrl)!.hasEdges = true;
        }
        if (nodeMap.has(targetUrl)) {
          nodeMap.get(targetUrl)!.hasEdges = true;
        }

        // Also add nodes from edges that might not be in allVertices (edge case)
        if (!nodeMap.has(sourceUrl)) {
          nodeMap.set(sourceUrl, {
            id: sourceUrl,
            label: extractLabel(sourceUrl),
            hasEdges: true,
            cluster: `domain:${new URL(sourceUrl).hostname}`
          });
        }
        if (!nodeMap.has(targetUrl)) {
          nodeMap.set(targetUrl, {
            id: targetUrl,
            label: extractLabel(targetUrl),
            hasEdges: true,
            cluster: `domain:${new URL(targetUrl).hostname}`
          });
        }
      });

      const nodes = Array.from(nodeMap.values());
      const links: GraphLink[] = edges.map(edge => ({
        source: edge.source.rendered,
        target: edge.target.rendered,
        info: edge.info
      }));

      console.log('[GraphVizPage] Nodes:', nodes.length, 'Links:', links.length);

      if (nodes.length === 0) {
        setError('No vertices to display. Add some vertices first.');
        setLoading(false);
        return;
      }

      // Fetch previews for all nodes using centralized API
      const nodeUrls = nodes.map(n => n.id);
      try {
        const previewData = await fetchPreviewsBatch(nodeUrls);
        console.log('[GraphVizPage] Preview data fetched for', Object.keys(previewData).length, 'nodes');

        // Update nodes with preview data
        nodes.forEach(node => {
          const preview = previewData[node.id];
          if (preview) {
            node.preview = preview;
            // Use preview title as label if available
            if (preview.title) {
              node.label = preview.title;
            }
          }
        });
      } catch (err) {
        console.error('[GraphVizPage] Failed to fetch previews:', err);
      }

      setGraphData({ nodes, links });
      setLoading(false);
    } catch (err) {
      console.error('[GraphVizPage] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load graph');
      setLoading(false);
    }
  }

  function extractLabel(url: string): string {
    try {
      const u = new URL(url);
      const path = u.pathname === '/' ? '' : u.pathname;
      return u.hostname + path;
    } catch (_) {
      return url.length > 40 ? url.slice(0, 40) + '...' : url;
    }
  }

  // Generate a color for a cluster based on its ID
  function getClusterColor(cluster: string): string {
    if (cluster.startsWith('domain:')) {
      return '#666'; // Dimmer color for domain clusters
    }
    // Generate a color from the set ID using a simple hash
    const setId = cluster.replace('set:', '');
    let hash = 0;
    for (let i = 0; i < setId.length; i++) {
      hash = setId.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Generate HSL color with good saturation and lightness for dark mode
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 60%, 55%)`;
  }

  function renderGraph(nodes: GraphNode[], links: GraphLink[]) {
    if (!svgRef.current || !containerRef.current) return;

    const svg = d3.select(svgRef.current);
    const rect = containerRef.current.getBoundingClientRect();
    const width = rect.width || 800;
    const height = rect.height || 600;

    console.log('[GraphVizPage] Rendering with dimensions:', width, height);

    // Save current zoom transform before clearing
    const currentTransform = d3.zoomTransform(svg.node()!);
    if (currentTransform.k !== 1 || currentTransform.x !== 0 || currentTransform.y !== 0) {
      zoomTransformRef.current = currentTransform;
    }

    svg.selectAll('*').remove();

    const defs = svg.append('defs');

    // Create drop shadow filter
    const filter = defs.append('filter')
      .attr('id', 'drop-shadow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    filter.append('feDropShadow')
      .attr('dx', 0)
      .attr('dy', 1)
      .attr('stdDeviation', 2)
      .attr('flood-opacity', 0.3);

    // Create arrow marker
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', '#999');

    // Calculate cluster positions
    const clusters = new Map<string, { nodes: GraphNode[]; label: string }>();
    nodes.forEach(node => {
      const cluster = node.cluster || 'domain:other';
      if (!clusters.has(cluster)) {
        const label = cluster.startsWith('set:')
          ? (node.setName || 'Set')
          : cluster.replace('domain:', '');
        clusters.set(cluster, { nodes: [], label });
      }
      clusters.get(cluster)!.nodes.push(node);
    });

    // Sort clusters: sets first (by size), then domains (by size)
    const sortedClusters = Array.from(clusters.entries()).sort((a, b) => {
      const aIsSet = a[0].startsWith('set:');
      const bIsSet = b[0].startsWith('set:');
      if (aIsSet && !bIsSet) return -1;
      if (!aIsSet && bIsSet) return 1;
      return b[1].nodes.length - a[1].nodes.length;
    });

    // Arrange clusters in a grid layout
    const clusterPositions = new Map<string, { x: number; y: number }>();
    const numClusters = sortedClusters.length;
    const cols = Math.ceil(Math.sqrt(numClusters));
    const rows = Math.ceil(numClusters / cols);
    const cellWidth = width / (cols + 1);
    const cellHeight = height / (rows + 1);

    sortedClusters.forEach(([clusterId], index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      clusterPositions.set(clusterId, {
        x: cellWidth * (col + 1),
        y: cellHeight * (row + 1)
      });
    });

    // Create simulation with clustering forces
    const simulation = d3.forceSimulation<GraphNode>(nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(links)
        .id(d => d.id)
        .distance(120))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('collision', d3.forceCollide().radius(55))
      .force('x', d3.forceX<GraphNode>()
        .x(d => clusterPositions.get(d.cluster || 'domain:other')?.x || width / 2)
        .strength(0.15))
      .force('y', d3.forceY<GraphNode>()
        .y(d => clusterPositions.get(d.cluster || 'domain:other')?.y || height / 2)
        .strength(0.15));

    // Store refs for direct manipulation
    simulationRef.current = simulation;
    nodesDataRef.current = nodes;
    linksDataRef.current = links;

    // Create container for zoom
    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Restore previous zoom transform if available
    if (zoomTransformRef.current) {
      svg.call(zoom.transform, zoomTransformRef.current);
    }

    // Draw links
    const linksGroup = g.append('g').attr('class', 'links');
    linksGroupRef.current = linksGroup;

    const link = linksGroup
      .selectAll('g')
      .data(links)
      .enter()
      .append('g')
      .attr('cursor', 'pointer')
      .on('contextmenu', (event, d) => {
        event.preventDefault();
        event.stopPropagation();
        setEdgeContextMenu({
          x: event.pageX,
          y: event.pageY,
          link: d
        });
      });

    // Add invisible wider line for easier click target
    link.append('line')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 12);

    // Add the visible line for each link
    link.append('line')
      .attr('stroke', d => d.info ? '#666' : '#999')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', d => d.info ? 2 : 1.5)
      .attr('stroke-dasharray', d => d.info ? '5,3' : 'none')
      .attr('marker-end', 'url(#arrowhead)');

    // Add tooltip for links with info
    link.filter(d => !!d.info)
      .append('title')
      .text(d => d.info!);

    // Draw cluster labels
    const clusterLabels = g.append('g')
      .attr('class', 'cluster-labels')
      .selectAll('text')
      .data(sortedClusters)
      .enter()
      .append('text')
      .attr('class', 'cluster-label')
      .attr('x', ([clusterId]) => clusterPositions.get(clusterId)?.x || 0)
      .attr('y', ([clusterId]) => (clusterPositions.get(clusterId)?.y || 0) - 60)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '500')
      .attr('fill', ([clusterId]) => {
        const color = getClusterColor(clusterId);
        return clusterId.startsWith('set:') ? color : '#888';
      })
      .attr('opacity', 0.8)
      .text(([, data]) => {
        const label = data.label;
        return label.length > 20 ? label.slice(0, 20) + '...' : label;
      })
      .append('title')
      .text(([, data]) => `${data.label} (${data.nodes.length} vertices)`);

    // Draw nodes
    const nodesGroup = g.append('g').attr('class', 'nodes');
    nodesGroupRef.current = nodesGroup;

    const node = nodesGroup
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        const currentConnectingFrom = connectingFromRef.current;
        if (currentConnectingFrom) {
          // In connection mode - complete the connection
          if (currentConnectingFrom.id !== d.id) {
            setEdgeCreation({ source: currentConnectingFrom, target: d });
          }
          setConnectingFrom(null);
        }
        // No navigation on click - use right-click context menu for "View details"
      })
      .on('contextmenu', (event, d) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
          x: event.pageX,
          y: event.pageY,
          node: d
        });
      })
      .call(d3.drag<SVGGElement, GraphNode>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    // Invisible circular hit area (inserted first so it's behind visible elements)
    node.append('circle')
      .attr('class', 'node-hit-area')
      .attr('r', d => (d.hasEdges ? 20 : 14) + 8)
      .attr('fill', 'transparent');

    // Add visible node content directly (no wrapper group to avoid foreignObject issues)
    node.each(function(d) {
      const g = d3.select(this);
      const nodeColor = getClusterColor(d.cluster || 'domain:other');
      const nodeRadius = d.hasEdges ? 20 : 14;

      if (d.preview?.image) {
        // Clip path for circular image
        const clipId = `clip-${d.id.replace(/[^a-zA-Z0-9]/g, '_')}`;
        g.append('clipPath')
          .attr('id', clipId)
          .append('circle')
          .attr('r', nodeRadius);

        // Glow ring (visible on hover when image shrinks)
        g.append('circle')
          .attr('class', 'node-ring')
          .attr('r', nodeRadius)
          .attr('fill', 'none')
          .attr('stroke', nodeColor)
          .attr('stroke-width', 3)
          .attr('opacity', 0)
          .style('transition', 'opacity 0.15s ease');

        // Image clipped to circle — default at 1.3x to fill, shrinks on hover
        const imgSize = nodeRadius * 2;
        g.append('image')
          .attr('class', 'node-visual')
          .attr('href', d.preview.image)
          .attr('x', -imgSize / 2)
          .attr('y', -imgSize / 2)
          .attr('width', imgSize)
          .attr('height', imgSize)
          .attr('preserveAspectRatio', 'xMidYMid slice')
          .attr('clip-path', `url(#${clipId})`)
          .style('transform-origin', '0 0')
          .style('transform', 'scale(1.3)')
          .style('transition', 'transform 0.15s ease');
      } else {
        g.append('circle')
          .attr('class', 'node-visual')
          .attr('r', nodeRadius)
          .attr('fill', nodeColor)
          .attr('opacity', d.hasEdges ? 1 : 0.7)
          .attr('filter', d.hasEdges ? 'url(#drop-shadow)' : 'none')
          .style('transition', 'transform 0.15s ease, r 0.15s ease');
      }
    });

    // Labels below the node (centered)
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => (d.hasEdges ? 20 : 14) + 16)
      .attr('font-size', '11px')
      .attr('fill', '#999')
      .attr('pointer-events', 'none')
      .text(d => {
        const label = d.label;
        return label.length > 25 ? label.slice(0, 25) + '\u2026' : label;
      })
      .append('title')
      .text(d => d.label);

    // Hover: shrink image to reveal ring, brighten label
    node
      .on('mouseenter', function() {
        const el = d3.select(this);
        el.select('image.node-visual')
          .style('transform', 'scale(1)');
        el.select('.node-ring')
          .attr('opacity', 1);
        el.select('circle.node-visual')
          .style('transform', 'scale(1.2)');
        el.select('text')
          .attr('fill', '#e5e5e5');
      })
      .on('mouseleave', function() {
        const el = d3.select(this);
        el.select('image.node-visual')
          .style('transform', 'scale(1.3)');
        el.select('.node-ring')
          .attr('opacity', 0);
        el.select('circle.node-visual')
          .style('transform', 'scale(1)');
        el.select('text')
          .attr('fill', '#999');
      });

    // Update positions on tick (use dynamic selection for newly added elements)
    simulation.on('tick', () => {
      // Select all link groups and update their lines
      if (linksGroupRef.current) {
        linksGroupRef.current.selectAll<SVGGElement, GraphLink>('g').selectAll('line')
          .attr('x1', function() {
            const d = d3.select(this.parentNode as SVGGElement).datum() as GraphLink;
            return (d.source as GraphNode).x!;
          })
          .attr('y1', function() {
            const d = d3.select(this.parentNode as SVGGElement).datum() as GraphLink;
            return (d.source as GraphNode).y!;
          })
          .attr('x2', function() {
            const d = d3.select(this.parentNode as SVGGElement).datum() as GraphLink;
            return (d.target as GraphNode).x!;
          })
          .attr('y2', function() {
            const d = d3.select(this.parentNode as SVGGElement).datum() as GraphLink;
            return (d.target as GraphNode).y!;
          });
      }

      // Update node positions
      if (nodesGroupRef.current) {
        nodesGroupRef.current.selectAll<SVGGElement, GraphNode>('g')
          .attr('transform', d => `translate(${d.x},${d.y})`);
      }
    });
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const fullscreenButton = (
    <button
      class="graph-viz-fullscreen-btn"
      onClick={toggleFullscreen}
      title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
    >
{isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
    </button>
  );

  if (loading) {
    return (
      <div class={`graph-viz-page ${isFullscreen ? 'fullscreen' : ''}`}>
        {fullscreenButton}
        <div class="loading">Loading graph...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div class={`graph-viz-page ${isFullscreen ? 'fullscreen' : ''}`}>
        {fullscreenButton}
        <div class="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div class={`graph-viz-page ${isFullscreen ? 'fullscreen' : ''}`}>
      {fullscreenButton}
      {connectingFrom && (
        <div class="graph-viz-connecting-hint">
          Click on another vertex to connect from "{connectingFrom.label.slice(0, 30)}..."
          <button onClick={() => setConnectingFrom(null)}>Cancel</button>
        </div>
      )}
      <div ref={containerRef} class="graph-viz-container">
        <svg ref={svgRef} class="graph-viz-svg" />
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          class="graph-context-menu"
          style={{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            class="graph-context-menu-item"
            onClick={() => {
              setConnectingFrom(contextMenu.node);
              setContextMenu(null);
            }}
          >
            Connect to...
          </button>
          <button
            class="graph-context-menu-item"
            onClick={() => {
              route(`/graph/${encodeURIComponent(contextMenu.node.id)}`);
              setContextMenu(null);
            }}
          >
            View details
          </button>
          <button
            class="graph-context-menu-item"
            onClick={() => {
              window.open(contextMenu.node.id, '_blank');
              setContextMenu(null);
            }}
          >
            Open URL
          </button>
        </div>
      )}

      {/* Edge Context Menu */}
      {edgeContextMenu && (
        <div
          class="graph-context-menu"
          style={{ left: `${edgeContextMenu.x}px`, top: `${edgeContextMenu.y}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            class="graph-context-menu-item graph-context-menu-item-danger"
            onClick={() => handleDeleteEdge(edgeContextMenu.link)}
          >
            Delete connection
          </button>
        </div>
      )}

      {/* Edge Creation Modal */}
      {edgeCreation && (
        <div class="edge-creation-modal-backdrop" onClick={() => setEdgeCreation(null)}>
          <div class="edge-creation-modal" onClick={(e) => e.stopPropagation()}>
            <button class="edge-creation-modal-close" onClick={() => setEdgeCreation(null)}>
              ×
            </button>
            <h3>Create Connection</h3>
            <div class="edge-creation-vertices">
              <div class="edge-creation-vertex">
                <span class="edge-creation-label">From:</span>
                <span class="edge-creation-title">
                  {edgeCreation.source.preview?.title || edgeCreation.source.label}
                </span>
              </div>
              <div class="edge-creation-arrow">→</div>
              <div class="edge-creation-vertex">
                <span class="edge-creation-label">To:</span>
                <span class="edge-creation-title">
                  {edgeCreation.target.preview?.title || edgeCreation.target.label}
                </span>
              </div>
            </div>
            <textarea
              class="edge-creation-note"
              placeholder="Add a note (optional)... Ctrl+Enter to submit"
              value={edgeNote}
              autoFocus
              onInput={(e) => setEdgeNote((e.target as HTMLTextAreaElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  handleCreateEdge();
                }
              }}
            />
            <div class="edge-creation-actions">
              <button class="btn-cancel" onClick={() => setEdgeCreation(null)}>
                Cancel
              </button>
              <button
                class="btn-confirm"
                onClick={handleCreateEdge}
                disabled={creatingEdge}
              >
                {creatingEdge ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
